import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/useToast";
import { eventsApi } from "@/lib/humhub-api/events";
import type { RecurrenceScope } from "@/lib/humhub-api/types";

interface UseDeleteAllParticipantsRemindersOptions {
  onSuccess?: () => void;
  onError?: () => void;
}

interface UseDeleteAllParticipantsRemindersReturn {
  isLoading: boolean;
  error: Error | null;
  deleteAllParticipantsReminders: (
    eventId: number,
    scope?: RecurrenceScope,
  ) => Promise<void>;
}

export const useDeleteAllParticipantsReminders = (
  options?: UseDeleteAllParticipantsRemindersOptions,
): UseDeleteAllParticipantsRemindersReturn => {
  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: async ({
      eventId,
      scope,
    }: {
      eventId: number;
      scope?: RecurrenceScope;
    }) => {
      return eventsApi.deleteAllParticipantsReminders(eventId, scope);
    },
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error) => {
      console.error("Error deleting all participants reminders:", error);
      options?.onError?.();
      toast({
        title: t("common.error"),
        description: t("eventManage.remindersDeleteFailed"),
        variant: "error",
      });
    },
  });

  const deleteAllParticipantsReminders = useCallback(
    async (eventId: number, scope?: RecurrenceScope): Promise<void> => {
      return mutation.mutateAsync({ eventId, scope });
    },
    [mutation],
  );

  return {
    isLoading: mutation.isPending,
    error: mutation.error,
    deleteAllParticipantsReminders,
  };
};
